import { For, Index, Show, createMemo, onCleanup } from "solid-js";
import { HandEnd_Outcome } from "../pb/mj/record/v1/record_pb";
import { num } from "../format";
import type { GameEnd, Win } from "../pb/mj/record/v1/record_pb";
import { PENALTY_CHONBO, PENALTY_FINE } from "../game/view";
import type { HandResult, View } from "../game/view";
import { handValue, isYakuman, item, limitKanji, limitOf, multipleKanji, yakuKanji } from "../game/items";
import { t } from "../strings";
import { toSignIn } from "../signin";
import { waits } from "../game/waits";
import { Countdown } from "./Countdown";
import { Tile } from "./Tile";
import { Dialog } from "./Dialog";
import type { EndSeat, Proposal, TableEnd } from "../api";
import { Flair, changeText, ratingText } from "./Flair";
import { rematchEndText } from "./RematchBubble";

const name = (v: View, seat: number) => {
  const n = v.players[seat]?.name || t.deletedPlayer; // a name is blank only for a deleted account
  return seat === v.seat ? `${n} (${t.you})` : n;
};

function Deltas(props: { v: View; deltas: readonly number[] }) {
  return (
    <table class="deltas">
      <thead>
        <tr>
          <For each={t.handEndColumns}>{(c) => <th>{c}</th>}</For>
        </tr>
      </thead>
      <tbody>
        <Index each={props.deltas}>
          {(d, s) => (
            <tr class={s === props.v.seat ? "self" : ""}>
              <td class="name">{name(props.v, s)}</td>
              <td class={d() > 0 ? "plus" : d() < 0 ? "minus" : ""}>{d() > 0 ? `+${num(d())}` : num(d())}</td>
              <td>{num(props.v.seats[s]?.points ?? 0)}</td>
            </tr>
          )}
        </Index>
      </tbody>
    </table>
  );
}

// A win as the end-of-hand screen breaks it down (design 8 item 1; design 15 §16.1 step 17): who won and from whom, the limit
// reached in kanji and romaji, what the hand pays as the headline, the hand large, the indicators, every yaku in kanji, romaji
// and English with its han, dora counted apart, then fu and han. A double ron's two are built alike.
function WinView(props: { v: View; w: Win; indicators: readonly number[] }) {
  const yakuman = () => props.w.items.some((i) => isYakuman(i.id));
  const limit = createMemo(() => limitOf(props.w, props.v.preset));
  const yaku = () => props.w.items.filter((i) => item(i.id).kind !== "dora");
  const dora = () => props.w.items.filter((i) => item(i.id).kind === "dora" && i.value > 0);
  // Who gave the call that completed a yakuman pays for it (pao): a tsumo is theirs alone, so it is not said as what each seat pays.
  const liable = () => (yakuman() && props.w.liabilitySeat < props.v.seats.length && props.w.liabilitySeat !== props.w.winner ? props.w.liabilitySeat : null);
  // The number large, its unit beside it.
  const paid = (): [string, string] => {
    const p = handValue(props.w, props.v.hand?.dealer ?? 0);
    if ("ron" in p) return [num(p.ron), t.points];
    if (liable() !== null) return [num("all" in p ? p.all * 3 : p.others * 2 + p.dealer), t.points];
    if ("all" in p) return [num(p.all), t.pointsAll];
    return [`${num(p.others)} / ${num(p.dealer)}`, t.points];
  };
  return (
    <div class={`win${limit().limit ? ` tier-${limit().limit}` : ""}`}>
      <div class="win-top">
      <h3>
        {name(props.v, props.w.winner)} — {props.w.selfDrawn ? t.tsumo : t.ronFrom(props.w.fromSeat === props.v.seat ? t.you : (props.v.players[props.w.fromSeat]?.name || t.deletedPlayer))}
      </h3>
      <Show when={limit().limit}>
        <p class="limit-name">
          <span class="limit-kanji" lang="ja">
            {limit().limit === "yakuman" ? (multipleKanji[limit().times] ?? `${limit().times}×`) : ""}
            {limitKanji[limit().limit]}
          </span>
          <span class="limit-romaji">
            {t.multiples[limit().times] ?? `${limit().times}× `}
            {t.limits[limit().limit]}
          </span>
        </p>
      </Show>
      <p class="win-paid">
        <strong>{paid()[0]}</strong> {paid()[1]}
      </p>
      <div class="win-hand">
        <For each={props.w.concealed?.ids.filter((id) => id !== props.w.winningTile)}>{(id) => <Tile id={id} red={props.v.redFives} size="s" />}</For>
        <span class="won">
          <Tile id={props.w.winningTile} red={props.v.redFives} size="s" />
        </span>
        <For each={props.w.melds}>
          {(m) => (
            <span class="meld">
              <For each={m.tiles}>{(id) => <Tile id={id} red={props.v.redFives} size="s" />}</For>
            </span>
          )}
        </For>
      </div>
      <Show when={props.indicators.length > 0}>
        <div class="indicators">
          <span class="label">{t.doraIndicators}</span>
          <For each={props.indicators}>{(id) => <Tile id={id} red={props.v.redFives} size="s" />}</For>
          <Show when={props.w.hiddenIndicators.length > 0}>
            <span class="label">{t.uraIndicators}</span>
            <For each={props.w.hiddenIndicators}>{(id) => <Tile id={id} red={props.v.redFives} size="s" />}</For>
          </Show>
        </div>
      </Show>
      </div>
      <div class="win-rest">
      <ul class="items">
        <For each={[...yaku(), ...dora()]}>
          {(i, at) => {
            const n = item(i.id);
            return (
              <li style={{ "--i": at() }}>
                <span class="yaku-kanji" lang="ja" aria-hidden="true">
                  {yakuKanji[i.id] ?? ""}
                </span>
                <span class="yaku" title={t.yakuHelp[i.id]}>
                  {n.romaji}
                  {n.english && n.english.toLowerCase() !== n.romaji.toLowerCase() && <small>{n.english}</small>}
                </span>
                <span class="han">{n.kind === "yakuman" ? t.yakuman : `${i.value} ${t.han}`}</span>
              </li>
            );
          }}
        </For>
      </ul>
      <Show when={!yakuman()}>
        <p class="score-line" style={{ "--i": yaku().length + dora().length }}>
          {`${props.w.fu > 0 ? `${props.w.fu} ${t.fu} ` : ""}${props.w.han} ${t.han}`}
        </p>
      </Show>
      <Show when={liable() !== null}>
        <p class="liable">{liable() === props.v.seat ? t.liableYou : t.liable(props.v.players[liable()!]?.name || t.deletedPlayer)}</p>
      </Show>
      </div>
    </div>
  );
}

/** What the button at a result's foot says: what comes next, and when it comes by itself. */
/** Marks a result's foot "more" while more of the result lies under it, to scroll to (UI review V5). */
function moreBelow(foot: HTMLElement): void {
  if (typeof ResizeObserver === "undefined") return;
  let box: HTMLElement | null = null;
  const set = () => box && foot.classList.toggle("more", box.scrollTop + box.clientHeight < box.scrollHeight - 2);
  const sizes = new ResizeObserver(set);
  queueMicrotask(() => {
    box = foot.closest(".dialog");
    if (!box) return;
    box.addEventListener("scroll", set, { passive: true });
    sizes.observe(box);
    if (foot.previousElementSibling) sizes.observe(foot.previousElementSibling);
    set();
  });
  onCleanup(() => {
    box?.removeEventListener("scroll", set);
    sizes.disconnect();
  });
}

function NextHand(props: { over: boolean; readBy?: number | null; onClose: () => void }) {
  const label = () => (props.over ? t.finalResult : t.nextHand);
  return (
    <button type="button" data-first onClick={() => props.onClose()}>
      <Show when={props.readBy} fallback={label()}>
        {(by) => <Countdown class="next-in" text={(now) => `${label()} · ${Math.max(0, Math.ceil((by() - now) / 1000))}`} />}
      </Show>
    </button>
  );
}

export function HandEndDialog(props: { v: View; r: HandResult; onClose: () => void; readBy?: number | null }) {
  const title = () => {
    const end = props.r.end;
    if (end?.outcome === HandEnd_Outcome.EXHAUSTIVE_DRAW) return t.draw;
    if (end?.outcome === HandEnd_Outcome.ABORTIVE_DRAW) return `${t.abortive}: ${t.aborts[props.r.abortive?.reason ?? 0] ?? ""}`;
    if (end?.outcome === HandEnd_Outcome.ABORTED_BY_SERVER) return t.aborted;
    if (end?.outcome === HandEnd_Outcome.VOIDED) {
      const last = props.v.penalties[props.v.penalties.length - 1];
      return last ? t.chonboTitle(props.v.players[last.seat]?.name ?? "") : t.voided;
    }
    return t.handEnd;
  };
  return (
    <Show when={props.r.end}>
      {(end) => {
        const deltas = () => end().points.map((p, s) => p - (props.r.pointsBefore[s] ?? p));
        return (
          <Dialog label={title()} class="hand-end" onClose={props.onClose}>
              <div class="hand-end-body">
              <h2 class={props.r.wins.length > 0 ? "kicker" : ""}>{title()}</h2>
              <For each={props.r.wins}>{(w) => <WinView v={props.v} w={w} indicators={props.v.hand?.indicators ?? []} />}</For>
              <Show when={props.r.exhaustive}>
                {(exhaustive) => (
                  <ul class="tenpai">
                    <Index each={exhaustive().ready}>
                      {(ready, s) => (
                        <li>
                          <span class="draw-seat">
                            {name(props.v, s)} <span class={ready() ? "ready" : "noten"}>{ready() ? t.tenpai : t.noten}</span>
                          </span>
                          <Show when={ready()}>
                            <span class="win-hand">
                              <For each={exhaustive().revealed[s]?.ids}>{(id) => <Tile id={id} red={props.v.redFives} size="s" />}</For>
                            </span>
                            {/* The kinds that would have won (a plain copy of each: the second, never a red five). */}
                            <span class="draw-waits">
                              <span class="label">{t.waits}</span>
                              <For each={waits(exhaustive().revealed[s]?.ids ?? [])}>{(k) => <Tile id={k * 4 + 1} red={false} size="s" />}</For>
                            </span>
                          </Show>
                        </li>
                      )}
                    </Index>
                  </ul>
                )}
              </Show>
              <For each={props.r.nagashi}>
                {(s) => <p class="liable">{s === props.v.seat ? t.nagashiYou : t.nagashi(props.v.players[s]?.name || t.deletedPlayer)}</p>}
              </For>
              <Show when={props.r.honba > 0 || props.r.sticks > 0}>
                <p class="counters">
                  {[props.r.honba > 0 && `${props.r.honba} ${t.honba}`, props.r.sticks > 0 && `${props.r.sticks} ${t.sticks(props.r.sticks)}`]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </Show>
              </div>
              {/* The yaku scroll; the points and what comes next don't: at a table with people the sheet closes in 15 s, and they are what most want to see. */}
              <div class="hand-end-foot" ref={moreBelow}>
                <Deltas v={props.v} deltas={deltas()} />
                <NextHand over={!!props.v.gameEnd} readBy={props.readBy} onClose={props.onClose} />
              </div>
          </Dialog>
        );
      }}
    </Show>
  );
}

// "812 → 824 (+12)", with "?" while provisional (design 14 §8.1).
const ratingStep = (s: EndSeat) => {
  const before = s.before_x100 ?? 0;
  const after = s.after_x100 ?? 0;
  return `${ratingText(before, s.provisional)} → ${ratingText(after, s.provisional)} (${changeText(after - before)})`;
};

// A game's changes of flair, the player's own first (design 14 §8.1).
function flairLines(state: TableEnd | null | undefined, me: string | undefined): string[] {
  if (!state?.flair_changes) return [];
  const rank = (f: string) => (f ? Number(f) : 1000);
  const of = (id: string) => state.seats.find((s) => s.public_id === id)?.name ?? "";
  const line = (c: { public_id: string; before: string; after: string }) => {
    const up = rank(c.after) < rank(c.before);
    if (c.public_id === me) {
      if (!up) return t.flairLost(t.flairNames[c.before] ?? "");
      return c.after === "1" ? t.flairTook : t.flairGained(t.flairNames[c.after] ?? "");
    }
    return up ? t.theirFlairGained(of(c.public_id), t.flairNames[c.after] ?? "") : t.theirFlairLost(of(c.public_id), t.flairNames[c.before] ?? "");
  };
  return [...state.flair_changes].sort((a, b) => Number(b.public_id === me) - Number(a.public_id === me)).map(line);
}

/** A game that ended with no end of it on this tab: the game server stopped (it cannot go on after that), or the tab was away. */
export function GameStoppedDialog(props: { aborted: boolean; onHome: () => void; onReplay: () => void }) {
  const title = () => (props.aborted ? t.gameStopped : t.gameOver);
  return (
    <Dialog label={title()} alert>
        <h2>{title()}</h2>
        <p>{props.aborted ? t.gameStoppedText : t.tableEnded}</p>
        <div class="row">
          <button type="button" onClick={() => props.onHome()}>
            {t.home}
          </button>
          {/* A game the server stopped is listed nowhere and cannot be opened (end_reason, design 7): no button to a dead end. */}
          <Show when={!props.aborted}>
            <button type="button" class="secondary" onClick={() => props.onReplay()}>
              {t.watchReplay}
            </button>
          </Show>
        </div>
    </Dialog>
  );
}

export function GameEndDialog(props: {
  v: View;
  end: GameEnd;
  onHome: () => void;
  onReplay?: () => void;
  onReview?: () => void;
  rematch?: { label: string; error: string; go: () => void }; // design 14 §8.2
  guest?: boolean; // not signed in: this game is kept by signing in (design 14 §9.2)
  state?: TableEnd | null; // what the server adds: ratings, presence (design 14 §8.1)
  proposal?: Proposal;
  onDecline?: () => void;
  onRankedAgain?: () => void;
  me?: string; // this player's public id
}) {
  const order = () => props.end.placement.map((p, s) => ({ p, s })).sort((a, b) => a.p - b.p || a.s - b.s);
  const seat = (s: number) => {
    const id = props.v.players[s]?.id ?? "";
    return id.startsWith("acct:") ? props.state?.seats.find((x) => x.public_id === id.slice(5)) : undefined;
  };
  const open = () => (props.proposal && !props.proposal.ended ? props.proposal : undefined);
  const answer = (s: EndSeat | undefined) => (s?.public_id ? open()?.votes.find((v) => v.public_id === s.public_id) : undefined);
  const placement = () => props.state?.kind === "placement";
  const mine = () => props.state?.seats.find((x) => x.public_id === props.me);
  return (
    <Dialog label={t.gameOver} alert>
        <h2>{t.gameOver}</h2>
        <Show when={props.end.placement[props.v.seat]}>
          {(p) => (
            <p class={`end-place place-${p()}`}>
              {t.finishedPlace[p() - 1]}
            </p>
          )}
        </Show>
        <table class="deltas final">
          <thead>
            <tr>
              <For each={t.gameEndColumns}>{(c) => <th>{c}</th>}</For>
            </tr>
          </thead>
          <tbody>
            <For each={order()}>
              {({ p, s }) => (
                <tr class={s === props.v.seat ? "self" : ""}>
                  <td>{t.place[p - 1]}</td>
                  <td class="name">
                    {name(props.v, s)} <Flair f={seat(s)?.flair} />
                    <Show when={seat(s)?.rated && props.state?.kind === "rated" ? seat(s) : undefined}>
                      {(st) => <span class="end-rating">{ratingStep(st())}</span>}
                    </Show>
                    <Show when={s !== props.v.seat && seat(s)?.presence}>
                      <span class={`end-presence ${seat(s)!.presence}`}>
                        {t.presenceEnd[seat(s)!.presence!]}
                        <Show when={answer(seat(s))}>{(a) => <> · {a().accepted ? t.rematchAccepted : t.rematchPending}</>}</Show>
                      </span>
                    </Show>
                  </td>
                  <td>{num(props.end.points[s] ?? 0)}</td>
                  <td class={(props.end.scoreX1000[s] ?? 0) >= 0 ? "plus" : "minus"}>{((props.end.scoreX1000[s] ?? 0) / 1000).toFixed(1)}</td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
        <p class="small">{t.finalScoreNote}</p>
        <For each={props.state?.kind === "rated" ? [...new Set(props.v.penalties.filter((x) => x.kind === PENALTY_CHONBO || x.kind === PENALTY_FINE).map((x) => x.seat))] : []}>
          {(s) => <p class="end-flair">{t.penalisedForRating(props.v.players[s]?.name ?? "")}</p>}
        </For>
        <Show when={placement()}>
          <p class="end-first-rating">
            <Show when={props.state?.settled && mine()?.rated} fallback={t.ratingReading}>
              {t.firstRating(ratingText(mine()!.after_x100 ?? 0, true))} {t.firstRatingAfter}
            </Show>
          </p>
        </Show>
        <For each={flairLines(props.state, props.me)}>{(line) => <p class="end-flair">{line}</p>}</For>
        <Show when={props.rematch?.error}>
          <p class="notice">{props.rematch!.error}</p>
        </Show>
        <Show when={props.proposal?.ended && props.proposal.ended !== "started"}>
          <p class="notice">{rematchEndText(props.proposal!)}</p>
        </Show>
        <Show when={open()}>
          {(p) => <p class="end-rematch">{p().accepted ? t.rematchWaiting(p().ready) : t.rematchAsked(p().proposer_name, p().ready)}</p>}
        </Show>
        <Show when={props.guest}>
          <p class="small">
            <a href="/signin" onClick={toSignIn}>{t.signInToKeep}</a>
          </p>
        </Show>
        <div class="row">
          <Show when={props.onReview}>
            <button type="button" onClick={() => props.onReview?.()}>
              {t.reviewThisGame}
            </button>
          </Show>
          <Show when={props.onRankedAgain}>
            <button type="button" class={props.onReview ? "secondary" : ""} onClick={() => props.onRankedAgain?.()}>
              {placement() ? t.playRanked : t.playRankedAgain}
            </button>
          </Show>
          <Show when={props.rematch && !placement() && !open()?.accepted}>
            <button type="button" class={props.onReview && !open() ? "secondary" : ""} onClick={() => props.rematch!.go()}>
              {open() ? t.accept : props.rematch!.label}
            </button>
          </Show>
          <Show when={open()}>
            <button type="button" class="secondary" onClick={() => props.onDecline?.()}>
              {open()!.accepted ? t.cancel : t.decline}
            </button>
          </Show>
        </div>
        <div class="row end-quiet">
          <button type="button" class="link" onClick={() => props.onHome()}>
            {t.home}
          </button>
          <Show when={props.onReplay}>
            <button type="button" class="link" onClick={() => props.onReplay?.()}>
              {t.watchReplay}
            </button>
          </Show>
        </div>
    </Dialog>
  );
}
